import { useMemo } from "react";

import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";

import { convertSunoPlaceholdersToMergeTags } from "utils/suno-merge-tags.util";

const URL_SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

// SN-19110: a link stored without a scheme ("google.com") resolves relative to the
// current page (…/patients/563/ + google.com) and navigates nowhere useful. Prepend
// https:// to bare external hrefs at render time so both old and newly-authored links
// open correctly. Schemes (http/https/mailto/tel), fragments, queries, and app/relative
// paths (/, #, ?, merge-tag tokens) are left untouched.
function normalizeExternalLinkHrefs(html: string): string {
  if (typeof document === "undefined" || !html.includes("<a")) {
    return html;
  }
  const container = document.createElement("div");
  container.innerHTML = html;
  container.querySelectorAll("a[href]").forEach((anchor) => {
    const href = (anchor.getAttribute("href") ?? "").trim();
    if (
      !href ||
      URL_SCHEME_RE.test(href) ||
      href.startsWith("/") ||
      href.startsWith("#") ||
      href.startsWith("?") ||
      href.startsWith("{{")
    ) {
      return;
    }
    anchor.setAttribute("href", `https://${href}`);
  });
  return container.innerHTML;
}

type WordProcessorHtmlContentProps = {
  html?: string | null;
  sx?: SxProps;
  dataReport?: string;
  dataTest?: string;
  dataTestId?: string;
};
export default function WordProcessorHtmlContent({
  dataTest,
  dataTestId,
  dataReport,
  html = "",
  sx = {}
}: WordProcessorHtmlContentProps) {
  const renderedHtml = useMemo(
    () => (html ? normalizeExternalLinkHrefs(convertSunoPlaceholdersToMergeTags(html)) : ""),
    [html]
  );
  return (
    <Box
      className="tiny-cloud-html"
      data-testid={dataTestId}
      data-test={dataTest}
      data-report={dataReport}
      sx={{
        wordBreak: "break-word",
        pt: 2,
        width: "100%",
        boxSizing: "border-box",
        "& img": {
          maxWidth: "100%",
          height: "auto",
          objectFit: "contain"
        },
        ...sx
      }}
      dangerouslySetInnerHTML={{
        __html: renderedHtml
      }}
    />
  );
}
